/**
 * Phase 11a: SOV Tab — displays or creates the Schedule of Values for a project.
 * R2: Includes Change Orders section below SOV line items.
 */
import React, { useState } from 'react';
import { useProjectSOV } from '@/hooks/useSOV';
import SOVModal from '@/components/invoices/SOVModal';
import ChangeOrdersSection from '@/components/projects/ChangeOrdersSection';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PlusCircle, Edit2, AlertCircle } from 'lucide-react';

interface SOVTabProps {
  projectId: string;
}

export default function SOVTab({ projectId }: SOVTabProps) {
  const { data, isLoading, isFetching, isError, error } = useProjectSOV(projectId);
  const [showModal, setShowModal] = useState(false);

  const sov = (data as any)?.sov;
  const has404 = (error as any)?.status === 404;

  if (isLoading) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Loading Schedule of Values…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Schedule of Values (SOV)</h3>
          <p className="text-sm text-muted-foreground">
            Define line items and holdback settings for progress (draw) invoicing.
          </p>
        </div>
        <Button
          size="sm"
          variant={sov ? 'outline' : 'default'}
          onClick={() => setShowModal(true)}
        >
          {sov ? (
            <>
              <Edit2 className="mr-1 h-3 w-3" />
              Replace SOV
            </>
          ) : (
            <>
              <PlusCircle className="mr-1 h-3 w-3" />
              Create SOV
            </>
          )}
        </Button>
      </div>

      {/* Error (non-404) */}
      {isError && !has404 && !isFetching && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Failed to load Schedule of Values. Please refresh.
        </div>
      )}

      {/* No SOV yet */}
      {(!sov) && !isLoading && !isError && (
        <div className="rounded-md border border-dashed p-8 text-center">
          <p className="text-sm text-muted-foreground mb-3">
            No Schedule of Values defined for this project.
          </p>
          <Button size="sm" onClick={() => setShowModal(true)}>
            <PlusCircle className="mr-1 h-3 w-3" />
            Create SOV
          </Button>
        </div>
      )}

      {/* SOV exists — show summary */}
      {sov && (
        <div className="space-y-3">
          {/* SOV meta */}
          <div className="flex flex-wrap gap-3 text-sm">
            <div>
              <span className="font-medium text-muted-foreground">Default Holdback:</span>{' '}
              <Badge variant="secondary">{sov.holdback_percent}%</Badge>
            </div>
            {sov.lender_name && (
              <div>
                <span className="font-medium text-muted-foreground">Lender:</span>{' '}
                {sov.lender_name}
              </div>
            )}
            {sov.loan_number && (
              <div>
                <span className="font-medium text-muted-foreground">Loan #:</span>{' '}
                {sov.loan_number}
              </div>
            )}
            <div>
              <span className="font-medium text-muted-foreground">Next Draw:</span>{' '}
              <Badge>#{sov.suggested_next_draw_number}</Badge>
            </div>
          </div>

          {/* Line items table */}
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">#</th>
                  <th className="px-3 py-2 text-left font-medium">Description</th>
                  <th className="px-3 py-2 text-left font-medium">Cost Code</th>
                  <th className="px-3 py-2 text-right font-medium">Budget</th>
                  <th className="px-3 py-2 text-right font-medium">Holdback %</th>
                </tr>
              </thead>
              <tbody>
                {(sov.line_items || []).map((li: any) => (
                  <tr key={li.id} className="border-t hover:bg-muted/30">
                    <td className="px-3 py-2 text-muted-foreground">{li.line_number}</td>
                    <td className="px-3 py-2">{li.description}</td>
                    <td className="px-3 py-2 font-mono text-xs">{li.cost_code}</td>
                    <td className="px-3 py-2 text-right">
                      ${Number(li.original_budget_amount).toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {li.holdback_percent != null
                        ? `${li.holdback_percent}%`
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t bg-muted/30">
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-right font-medium text-sm">Total Budget</td>
                  <td className="px-3 py-2 text-right font-semibold">
                    ${(sov.line_items || []).reduce(
                      (sum: number, li: any) => sum + Number(li.original_budget_amount),
                      0
                    ).toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Create / Replace modal */}
      {showModal && (
        <SOVModal
          projectId={projectId}
          existingSov={sov}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Change Orders — always shown below SOV */}
      <ChangeOrdersSection projectId={projectId} />
    </div>
  );
}
